import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";

// Import SVG icons
import TrashIcon from "../assets/icons/trash.svg";
import EyeIcon from "../assets/icons/eye.svg";

import styles from './styles/SnippetsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Snippet {
  snippet_id: number;
  curriculum: string;
  period_start: string;
  period_end: string;
  status: string;
}

interface Member {
  user_id: number;
  username: string;
  display_name?: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

const SnippetsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [error, setError] = useState<string>("");
  const [snippetToDelete, setSnippetToDelete] = useState<Snippet | null>(null);

  useEffect(() => {
    const fetchMemberInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const data = await response.json();
        setMemberInfo(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberInfo();
    }
  }, [member_id, token, selectedOrgID]);

  useEffect(() => {
    const fetchSnippets = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/snippets?user_id=${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch snippets.");
          return;
        }

        const data = await response.json();
        setSnippets(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchSnippets();
    }
  }, [member_id, token, selectedOrgID]);

  const handleDelete = async (snippetId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets/${snippetId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete snippet.");
        return;
      }

      setSnippets(snippets.filter(s => s.snippet_id !== snippetId));
      setSnippetToDelete(null);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Snippets</h1>
      {memberInfo && (
        <p className={styles.memberInfo}>
          <strong>Member:</strong>{" "}
          <a href={`/members/${member_id}`} className={styles.memberLink}>
            {memberInfo.display_name 
              ? `${memberInfo.display_name} (${memberInfo.username})`
              : memberInfo.username}
          </a>
        </p>
      )}

      <button 
        onClick={() => navigate(`/members/${member_id}/snippets/create`)}
        className={styles.generateButton}
      >
        Create Snippet
      </button>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Period</th>
            <th className={styles.tableHeader}>Status</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {snippets.length > 0 ? (
            snippets.map((snippet) => (
              <tr key={snippet.snippet_id}>
                <td className={styles.tableCell}>
                  {snippet.status === "processing" ? (
                    `${new Date(snippet.period_start).toLocaleDateString()} - ${new Date(snippet.period_end).toLocaleDateString()}`
                  ) : (
                    `${new Date(snippet.period_start).toLocaleDateString()} - ${new Date(snippet.period_end).toLocaleDateString()}`
                  )}
                </td>
                <td className={styles.tableCell}>
                  {snippet.status === "processing" ? "Processing..." :
                   snippet.status === "draft" ? "Draft" :
                   snippet.status === "sent" ? "Sent" : snippet.status}
                </td>
                <td className={styles.tableCell}>
                  <div className={styles.actionButtons}>
                    {snippet.status !== "processing" && (
                      <button
                        className={styles.iconButton}
                        onClick={() => navigate(`/members/${member_id}/snippets/${snippet.snippet_id}`)}
                        title="View"
                      >
                        <img src={EyeIcon} alt="View" className={styles.actionIcon} />
                      </button>
                    )}
                    <button
                      className={styles.iconButton}
                      onClick={() => setSnippetToDelete(snippet)}
                      title="Delete"
                    >
                      <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className={styles.tableCell}>No snippets found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {snippetToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setSnippetToDelete(null)} />
          <div className={styles.dialog}>
            <p>
              Are you sure you want to delete this snippet?<br /><br />
              This action CANNOT BE UNDONE.
            </p>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(snippetToDelete.snippet_id)} className={styles.dialogButton}>Yes, Delete</button>
              <button onClick={() => setSnippetToDelete(null)} className={styles.dialogCancelButton}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SnippetsPage; 